import React, { HTMLProps } from "react";
import { useTranslation } from "react-i18next";
import Logo from "../../../resources/images/logo.svg";
import tenantService from "../../services/TenantService";
import { ContentConfig, NavigationPage } from "../../types/ContentConfig";
import { readLanguageFromCookie } from "../GeneralUtils";
import "./Footer.scss";

import LOGO_CH_EOS_DE_EN from "../../../resources/images/ch-eos/member_of_inkasso_suisse_white.png";
import LOGO_CH_EOS_FR from "../../../resources/images/ch-eos/member_of_recouvrement_suisse_white.png";
import LOGO_CH_EOS_IT from "../../../resources/images/ch-eos/member_of_recupero_suisse_white.png";
import LOGO_DE_DID_BDIU from "../../../resources/images/de-did/logo_bdiu.svg";

const LOGO_FR_FIGEC = "https://cdn.eos-france.com/images/LogoFIGEC23-white.png";

const Footer = () => {
    const { t } = useTranslation();
    const [config, setConfig] = React.useState<ContentConfig>();
    const [tenant, setTenant] = React.useState<string>("");
    const currentYear = new Date().getFullYear();
    const lang = readLanguageFromCookie();

    React.useEffect(() => {
        initData();
    }, []);

    const initData = async () => {
        const tenant = await tenantService.getTenant();
        const config = await tenantService.getConfig(tenant);

        setTenant(tenant);
        setConfig(config);
    };

    const isFooterLinkAvailable = (navigation: NavigationPage): boolean => {
        if (!config) {
            return false;
        }

        return config.footerLinks.includes(navigation);
    };

    return (
        <div className="footer">
            <div className="footer-upper-nav">
                <div className="footer-nav">
                    <div className={"eos-footer-logo"}>
                        <a href="/">
                            <img src={Logo} alt={"eos logo"} height={26} />
                        </a>
                    </div>
                    <nav className="footer-links">
                        <ul>
                            {isFooterLinkAvailable(NavigationPage.IMPRINT_OLD) && (
                                <li>
                                    <FooterLink href={"/content/legal_info"} label={t("FOOTER.IMPRINT")} show={isFooterLinkAvailable(NavigationPage.IMPRINT_OLD)} />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.IMPRINT) && (
                                <li>
                                    <FooterLink href={"/services/content/legalNotice.html"} label={t("FOOTER.IMPRINT")} show={isFooterLinkAvailable(NavigationPage.IMPRINT)} />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.PRIVACY_OLD) && (
                                <li>
                                    <FooterLink href={"/services/content/privacy.html"} label={t("FOOTER.PRIVACY")} show={isFooterLinkAvailable(NavigationPage.PRIVACY_OLD)} />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.PRIVACY) && (
                                <li>
                                    <FooterLink href={"/services/content/privacyPolicy.html"} label={t("FOOTER.PRIVACY")} show={isFooterLinkAvailable(NavigationPage.PRIVACY)} />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.RATE_AGREEMENT) && (
                                <li>
                                    <FooterLink
                                        href={"/services/rateagreement/ratenzahlung"}
                                        label={t("FOOTER.RATEAGREEMENT")}
                                        show={isFooterLinkAvailable(NavigationPage.RATE_AGREEMENT)}
                                    />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.COOKIE_POLICY) && (
                                <li>
                                    <FooterLink
                                        href={"/services/content/cookiePolicy.html"}
                                        label={t("FOOTER.COOKIE_POLICY")}
                                        show={isFooterLinkAvailable(NavigationPage.COOKIE_POLICY)}
                                    />
                                </li>
                            )}
                        </ul>
                        <ul>
                            {isFooterLinkAvailable(NavigationPage.EAA_PRIVACY) && (
                                <li>
                                    <FooterLink
                                        href={"/services/content/eaaprivacyPage.html"}
                                        label={t("FOOTER.EAA_PRIVACY")}
                                        show={isFooterLinkAvailable(NavigationPage.EAA_PRIVACY)}
                                    />
                                </li>
                            )}
                            {isFooterLinkAvailable(NavigationPage.EAA_SIMPLE_LANGUAGE) && (
                                <li>
                                    <FooterLink
                                        href={"/services/content/easyLanguage.html"}
                                        label={t("FOOTER.EAA_SIMPLE_LANGUAGE")}
                                        show={isFooterLinkAvailable(NavigationPage.EAA_SIMPLE_LANGUAGE)}
                                    />
                                </li>
                            )}
                        </ul>
                    </nav>

                    {/* desktop logo */}
                    {determineFooterLogo(tenant, lang) != "" && <FooterLogo src={determineFooterLogo(tenant, lang)} alt={t("FOOTER.LOGO_ALT")} />}
                </div>
            </div>

            <div className={"footer-copyright"} role={"contentinfo"} aria-label="COPYRIGHT">
                <p>&copy; {currentYear} EOS</p>

                {/* mobile logo */}
                <FooterLogo src={determineFooterLogo(tenant, lang)} />
            </div>
        </div>
    );
};

type FooterLinkProps = {
    href: string;
    label: string;
    show: boolean;
};

const FooterLink: React.FC<FooterLinkProps> = (props) => {
    if (props.show) {
        return <a href={props.href}>{props.label}</a>;
    } else {
        return <></>;
    }
};

const FooterLogo: React.FC<HTMLProps<HTMLImageElement>> = ({ alt = "", src }) => {
    return <img alt={alt} src={src} className="additional-footer-logo" />;
};

function determineFooterLogo(tenant: string, language: string) {
    if (tenant === "de-did") {
        return LOGO_DE_DID_BDIU;
    }

    if (tenant === "es-eos") {
        return "";
    }

    if (tenant === "be-eos") {
        return "";
    }

    if (tenant === "ch-eos") {
        switch (language) {
            case "fr":
                return LOGO_CH_EOS_FR;

            case "it":
                return LOGO_CH_EOS_IT;

            case "de":
            case "en":
            default:
                return LOGO_CH_EOS_DE_EN;
        }
    }

    if (tenant === "fr-eos") {
        return LOGO_FR_FIGEC;
    }
}

export default Footer;
